import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const ResourcesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  padding: 3rem 2rem;
  border-radius: 16px;
  margin: 0 0 3rem 0;
  
  @media (max-width: 767px) {
    margin: 0 -2rem 3rem -2rem;
    border-radius: 0;
  }
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
`;

const ResourcesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const ResourceCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  border-left: 4px solid ${props => props.theme.colors.secondary};
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
  }
`;

const ResourceTitle = styled.h3`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  font-size: 1.5rem;
`;

const ResourceDescription = styled.p`
  color: ${props => props.theme.colors.gray};
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const ResourceLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ResourceLink = styled.a`
  color: ${props => props.theme.colors.secondary};
  text-decoration: none;
  font-weight: bold;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e2e8f0;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    text-decoration: underline;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const EmergencySection = styled(motion.section)`
  background: #fff5f5;
  border: 2px solid #fed7d7;
  border-radius: 12px;
  padding: 2rem;
  margin: 3rem 0;
  text-align: center;
`;

const EmergencyTitle = styled.h2`
  color: #c53030;
  margin-bottom: 1rem;
  font-size: 2rem;
`;

const EmergencyNumber = styled.div`
  font-size: 4rem;
  font-weight: bold;
  color: #c53030;
  margin: 1rem 0;
`;

const LocalResourcesSection = styled(motion.section)`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  margin: 2rem 0;
`;

const NextSectionContainer = styled.div`
  text-align: center;
  margin-top: 4rem;
  padding: 2rem;
`;

const NextSectionLink = styled(Link)`
  display: inline-block;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: bold;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const NextSectionText = styled.p`
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.gray};
`;

const Resources = () => {
  const resourceCategories = [
    {
      title: "CPR Training",
      description: "Learn life-saving CPR skills that could help save someone like Maggie. Classes are available locally and online.",
      links: [
        { text: "American Heart Association CPR Classes", url: "https://cpr.heart.org/en/course-catalog-search" },
        { text: "Red Cross CPR Training", url: "https://www.redcross.org/take-a-class/cpr" },
        { text: "Find Local CPR Classes", url: "https://www.heart.org/en/cpr" }
      ]
    },
    {
      title: "AED Training",
      description: "Automated External Defibrillators can restart the heart. Learn how to use these life-saving devices.",
      links: [
        { text: "AED Training Programs", url: "https://www.heart.org/en/cpr/aed" },
        { text: "Find AEDs Near You", url: "https://www.aedlocator.com" },
        { text: "AED Maintenance Guide", url: "https://www.redcross.org/get-help/how-to-prepare-for-emergencies/types-of-emergencies/aed" }
      ]
    },
    {
      title: "Heart Health",
      description: "Learn about heart health, prevention, and recognizing the signs of cardiac emergencies.",
      links: [
        { text: "American Heart Association", url: "https://www.heart.org" },
        { text: "Heart Disease Prevention", url: "https://www.cdc.gov/heartdisease/prevention.htm" },
        { text: "Warning Signs of Heart Attack", url: "https://www.heart.org/en/health-topics/heart-attack/warning-signs-of-a-heart-attack" }
      ]
    },
    {
      title: "Emergency Apps",
      description: "Mobile apps that can help in emergency situations and provide quick access to life-saving information.",
      links: [
        { text: "Red Cross Emergency App", url: "https://www.redcross.org/get-help/how-to-prepare-for-emergencies/mobile-apps" },
        { text: "PulsePoint AED Locator", url: "https://www.pulsepoint.org" },
        { text: "First Aid by Red Cross", url: "https://www.redcross.org/mobile-apps/first-aid-app" }
      ]
    },
    {
      title: "First Aid Training",
      description: "Comprehensive first aid training that includes cardiac emergency response and other life-saving skills.",
      links: [
        { text: "Red Cross First Aid Courses", url: "https://www.redcross.org/take-a-class/first-aid" },
        { text: "Wilderness First Aid", url: "https://www.nols.edu/en/coursefinder/courses/wilderness-first-aid-WFA/" },
        { text: "Pediatric First Aid", url: "https://www.heart.org/en/cpr/pediatric-first-aid" }
      ]
    },
    {
      title: "Medical Support",
      description: "Resources for cardiac patients and families dealing with sudden cardiac events.",
      links: [
        { text: "Sudden Cardiac Arrest Foundation", url: "https://www.sca-aware.org" },
        { text: "Heart Rhythm Society", url: "https://www.hrsonline.org" },
        { text: "Support Groups", url: "https://www.heart.org/en/get-involved/ways-to-give/support-groups" }
      ]
    }
  ];

  return (
    <ResourcesContainer>
      <PageHeader>
        <PageTitle>Heart Health Resources</PageTitle>
        <PageSubtitle>
          Learn the skills and knowledge that could help you save a life. 
          Every second counts in a cardiac emergency.
        </PageSubtitle>
      </PageHeader>

      <EmergencySection
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <EmergencyTitle>🚨 Cardiac Emergency</EmergencyTitle>
        <p style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>
          If someone collapses and is unresponsive:
        </p>
        <EmergencyNumber>911</EmergencyNumber>
        <div style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
          <p><strong>1. Call 911 immediately</strong></p>
          <p><strong>2. Start CPR if trained</strong></p>
          <p><strong>3. Look for an AED nearby</strong></p>
          <p><strong>4. Continue until help arrives</strong></p>
        </div>
      </EmergencySection>

      <ResourcesGrid>
        {resourceCategories.map((resource, index) => (
          <ResourceCard
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            viewport={{ once: true }}
          >
            <ResourceTitle>{resource.title}</ResourceTitle>
            <ResourceDescription>{resource.description}</ResourceDescription>
            <ResourceLinks>
              {resource.links.map((link, linkIndex) => (
                <ResourceLink
                  key={linkIndex}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.text} →
                </ResourceLink>
              ))}
            </ResourceLinks>
          </ResourceCard>
        ))}
      </ResourcesGrid>

      <LocalResourcesSection
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <h2 style={{ color: '#2c5282', marginBottom: '1.5rem' }}>
          Utah & Saratoga Springs Resources
        </h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
          <div>
            <h3 style={{ color: '#3182ce', marginBottom: '1rem' }}>Local Emergency Services</h3>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Saratoga Springs Fire Department</strong><br/>
                <span style={{ color: '#718096' }}>Emergency: 911</span>
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Holy Cross Hospital - Mountain Point</strong><br/>
                <em>Initial emergency care and ICU treatment</em><br/>
                <br/>
                <strong>St. Mark's Hospital</strong><br/>
                <em>ICD implantation and specialized cardiac care</em><br/>
                <span style={{ color: '#718096' }}>1055 N 500 W, Lehi, UT</span>
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Utah County Emergency Services</strong><br/>
                <span style={{ color: '#718096' }}>Non-emergency: (801) 851-4000</span>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 style={{ color: '#3182ce', marginBottom: '1rem' }}>CPR Training in Utah</h3>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li style={{ marginBottom: '0.5rem' }}>
                <ResourceLink href="https://utahcpr.com" target="_blank" rel="noopener noreferrer">
                  Utah CPR Training
                </ResourceLink>
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <ResourceLink href="https://www.utahredcross.org" target="_blank" rel="noopener noreferrer">
                  Utah Red Cross
                </ResourceLink>
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <ResourceLink href="https://health.utah.gov" target="_blank" rel="noopener noreferrer">
                  Utah Department of Health
                </ResourceLink>
              </li>
            </ul>
          </div>
        </div>
        
        <div style={{ marginTop: '2rem', padding: '1.5rem', backgroundColor: '#f7fafc', borderRadius: '8px' }}>
          <h4 style={{ color: '#2c5282', marginBottom: '1rem' }}>Inspired by Maggie's Story?</h4>
          <p style={{ color: '#4a5568', lineHeight: '1.6' }}>
            Consider getting CPR certified in honor of Maggie's miraculous survival. 
            Your training could be the difference between life and death for someone in your community. 
            Eliza's recent CPR recertification literally saved her mother's life.
          </p>
        </div>
      </LocalResourcesSection>

      <NextSectionContainer>
        <NextSectionText>Continue exploring the complete story</NextSectionText>
        <NextSectionLink to="/videos">Watch Emergency Response Videos</NextSectionLink>
      </NextSectionContainer>
    </ResourcesContainer>
  );
};

export default Resources;
